// src/component/AdminGroupAllocation2026Watch.js
//
// 2026 グループ分け 確認画面（/admin/group-allocation-2026-watch）
//
//  二人体制で振り分け作業をしている際に、リアルタイムで
//  「誰がどのグループに入ったか」を確認するための read-only ビュー。
//  - groupActivity2026Groups コレクションを onSnapshot で購読
//  - 変更があったグループは 30 秒間ハイライト表示
//  - グループ名 / メンバー名 で全文検索
//  - 確定 / 下書き でフィルタ
//
//  編集は AdminGroupAllocation2026（/admin/group-allocation-2026）側で。

import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Avatar,
  Tooltip,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import SearchIcon from "@mui/icons-material/Search";
import FiberManualRecordRoundedIcon from "@mui/icons-material/FiberManualRecordRounded";

import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../api/firebase";
import { useNavigate, Link as RouterLink } from "react-router-dom";

import { AppThemeContext } from "./AppShell";

const HIGHLIGHT_DURATION_MS = 30 * 1000;

function normalizeMember(m) {
  if (typeof m === "string") return { uid: m };
  return m || {};
}

function projectGroup(id, data) {
  const rawMembers = Array.isArray(data.members) ? data.members : [];
  return {
    id,
    name: data.name || "",
    region: data.region || "",
    status: data.status || "draft",
    cohort: data.cohort || "normal",
    members: rawMembers.map(normalizeMember),
    createdAtMs:
      typeof data.createdAt?.toMillis === "function"
        ? data.createdAt.toMillis()
        : 0,
    updatedAtMs:
      typeof data.updatedAt?.toMillis === "function"
        ? data.updatedAt.toMillis()
        : 0,
  };
}

function formatTimestamp(d) {
  if (!d) return "";
  return d.toLocaleTimeString("ja-JP", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export default function AdminGroupAllocation2026Watch() {
  const navigate = useNavigate();
  const theme = useContext(AppThemeContext) || {};
  const accent = theme.railActive ?? "#5A35FF";
  const surface = theme.surfaceBg ?? "#FFFFFF";
  const textBase = theme.railText ?? "rgba(40,40,60,0.9)";
  const border = theme.bottomBorder ?? "rgba(0,0,0,0.08)";

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [groups, setGroups] = useState([]);
  const [recentlyChanged, setRecentlyChanged] = useState(() => new Set());
  const [lastUpdate, setLastUpdate] = useState(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | finalized | draft

  // ── realtime subscription ──
  useEffect(() => {
    const timeouts = [];
    const unsub = onSnapshot(
      collection(db, "groupActivity2026Groups"),
      (snap) => {
        const acc = [];
        snap.forEach((d) => {
          acc.push(projectGroup(d.id, d.data() || {}));
        });

        // 変更検出（初回は全部 "added" で来るので、初回は無視）
        const isFirstLoad = loading;
        const changedIds = [];
        if (!isFirstLoad) {
          snap.docChanges().forEach((change) => {
            if (change.type === "modified" || change.type === "added") {
              changedIds.push(change.doc.id);
            }
          });
        }

        setGroups(acc);
        setLastUpdate(new Date());
        setLoading(false);
        setErrorMsg("");

        if (changedIds.length > 0) {
          setRecentlyChanged((prev) => {
            const next = new Set(prev);
            changedIds.forEach((id) => next.add(id));
            return next;
          });
          const t = setTimeout(() => {
            setRecentlyChanged((prev) => {
              const next = new Set(prev);
              changedIds.forEach((id) => next.delete(id));
              return next;
            });
          }, HIGHLIGHT_DURATION_MS);
          timeouts.push(t);
        }
      },
      (err) => {
        console.error("watch onSnapshot error:", err);
        setErrorMsg("リアルタイム購読に失敗しました。権限をご確認ください。");
        setLoading(false);
      }
    );
    return () => {
      unsub();
      timeouts.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── derived ──
  const stats = useMemo(() => {
    let totalMembers = 0;
    let finalized = 0;
    let draft = 0;
    groups.forEach((g) => {
      totalMembers += g.members.length;
      if (g.status === "finalized") finalized++;
      else draft++;
    });
    return { total: groups.length, finalized, draft, totalMembers };
  }, [groups]);

  // ── 重複検知（確定グループのみ対象）──
  // 同じ uid が複数の「確定」グループに在籍している uid の集合。
  const duplicateUids = useMemo(() => {
    const count = new Map();
    groups.forEach((g) => {
      if (g.status !== "finalized") return;
      g.members.forEach((m) => {
        if (!m.uid) return;
        count.set(m.uid, (count.get(m.uid) || 0) + 1);
      });
    });
    const dup = new Set();
    count.forEach((n, uid) => {
      if (n >= 2) dup.add(uid);
    });
    return dup;
  }, [groups]);

  // 重複が含まれるグループ数（chip 表示用、確定グループのみ対象）
  const dupGroupCount = useMemo(() => {
    if (duplicateUids.size === 0) return 0;
    return groups.filter(
      (g) =>
        g.status === "finalized" &&
        g.members.some((m) => duplicateUids.has(m.uid))
    ).length;
  }, [groups, duplicateUids]);

  const filtered = useMemo(() => {
    let arr = [...groups];
    if (statusFilter === "finalized") {
      arr = arr.filter((g) => g.status === "finalized");
    } else if (statusFilter === "draft") {
      arr = arr.filter((g) => g.status !== "finalized");
    } else if (statusFilter === "duplicates") {
      arr = arr.filter(
        (g) =>
          g.status === "finalized" &&
          g.members.some((m) => duplicateUids.has(m.uid))
      );
    }
    const s = search.trim().toLowerCase();
    if (s) {
      arr = arr.filter((g) => {
        if (g.name.toLowerCase().includes(s)) return true;
        if ((g.region || "").toLowerCase().includes(s)) return true;
        return g.members.some(
          (m) =>
            (m.name || "").toLowerCase().includes(s) ||
            (m.uid || "").toLowerCase().includes(s)
        );
      });
    }
    // 最近更新されたものを先頭、その後 updatedAt降順
    arr.sort((a, b) => {
      const aR = recentlyChanged.has(a.id) ? 1 : 0;
      const bR = recentlyChanged.has(b.id) ? 1 : 0;
      if (aR !== bR) return bR - aR;
      if (b.updatedAtMs !== a.updatedAtMs)
        return b.updatedAtMs - a.updatedAtMs;
      return a.name.localeCompare(b.name, "ja");
    });
    return arr;
  }, [groups, search, statusFilter, recentlyChanged, duplicateUids]);

  return (
    <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
      {/* ヘッダー */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate("/admin")} size="small">
          <ArrowBackRoundedIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography
            variant="overline"
            sx={{
              letterSpacing: "0.18em",
              fontSize: 11,
              opacity: 0.8,
              color: textBase,
            }}
          >
            ADMIN
          </Typography>
          <Typography
            variant="h5"
            sx={{ fontWeight: 800, letterSpacing: "0.03em", color: textBase }}
          >
            2026 グループ分け 確認
          </Typography>
        </Box>
      </Stack>

      <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.65)", mb: 2 }}>
        グループ編成の進行をリアルタイムで監視する閲覧専用画面です。
        編集は「2026グループ分け」画面で行ってください。
      </Typography>

      {/* ステータスバー */}
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={0.5} alignItems="center">
          <FiberManualRecordRoundedIcon
            sx={{
              fontSize: 12,
              color: errorMsg ? "#d32f2f" : "#2e7d32",
              animation: errorMsg ? "none" : "pulse 2s infinite",
              "@keyframes pulse": {
                "0%, 100%": { opacity: 1 },
                "50%": { opacity: 0.3 },
              },
            }}
          />
          <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.6)" }}>
            {errorMsg ? "切断" : "リアルタイム購読中"}
            {lastUpdate && ` · 最終更新 ${formatTimestamp(lastUpdate)}`}
          </Typography>
        </Stack>
        <Chip
          size="small"
          label={`グループ合計 ${stats.total}`}
          sx={{
            bgcolor: `${accent}14`,
            color: accent,
            border: `1px solid ${accent}33`,
            fontWeight: 800,
          }}
        />
        <Chip
          size="small"
          label={`確定 ${stats.finalized}`}
          sx={{
            bgcolor: "#2e7d3214",
            color: "#2e7d32",
            border: "1px solid #2e7d3233",
            fontWeight: 800,
          }}
        />
        <Chip
          size="small"
          label={`下書き ${stats.draft}`}
          sx={{
            bgcolor: "rgba(0,0,0,0.06)",
            color: "rgba(0,0,0,0.65)",
            fontWeight: 800,
          }}
        />
        <Chip
          size="small"
          label={`総メンバー ${stats.totalMembers}`}
          sx={{
            bgcolor: "#b0880014",
            color: "#b08800",
            border: "1px solid #b0880033",
            fontWeight: 800,
          }}
        />
        {dupGroupCount > 0 && (
          <Chip
            size="small"
            label={`重複あり ${dupGroupCount}グループ（${duplicateUids.size}人）`}
            sx={{
              bgcolor: "#d32f2f14",
              color: "#d32f2f",
              border: "1px solid #d32f2f55",
              fontWeight: 800,
            }}
          />
        )}
      </Stack>

      {errorMsg && (
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            bgcolor: "rgba(220,60,60,0.08)",
            border: "1px solid rgba(220,60,60,0.3)",
            color: "rgb(160,30,30)",
            fontSize: 14,
          }}
        >
          {errorMsg}
        </Paper>
      )}

      {/* フィルタ */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 2,
          borderRadius: 3,
          border: `1px solid ${border}`,
          bgcolor: surface,
          boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <TextField
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="グループ名／メンバー名／地域で検索"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 320, flexGrow: 1 }}
          />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={statusFilter}
            onChange={(_, v) => {
              if (v) setStatusFilter(v);
            }}
            sx={{
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontWeight: 700,
                py: 0.25,
                px: 1.25,
              },
              "& .Mui-selected": {
                bgcolor: `${accent}14 !important`,
                color: `${accent} !important`,
              },
            }}
          >
            <ToggleButton value="all">すべて</ToggleButton>
            <ToggleButton value="finalized">確定</ToggleButton>
            <ToggleButton value="draft">下書き</ToggleButton>
            <ToggleButton
              value="duplicates"
              disabled={dupGroupCount === 0}
              sx={{
                color: dupGroupCount > 0 ? "#d32f2f !important" : undefined,
                "&.Mui-selected": {
                  bgcolor: "#d32f2f14 !important",
                  color: "#d32f2f !important",
                },
              }}
            >
              重複あり
              {dupGroupCount > 0 ? `（${dupGroupCount}）` : ""}
            </ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.55)" }}>
            表示 {filtered.length} / 全 {groups.length}
          </Typography>
        </Stack>
      </Paper>

      {/* 本体 */}
      {loading ? (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            textAlign: "center",
            borderRadius: 3,
            border: `1px solid ${border}`,
            bgcolor: surface,
            color: "rgba(0,0,0,0.6)",
          }}
        >
          {groups.length === 0
            ? "まだグループはありません。"
            : "該当するグループはありません。"}
        </Paper>
      ) : (
        <Stack spacing={1.25}>
          {filtered.map((g) => (
            <GroupRow
              key={g.id}
              group={g}
              isHot={recentlyChanged.has(g.id)}
              accent={accent}
              border={border}
              surface={surface}
              textBase={textBase}
              search={search}
              duplicateUids={duplicateUids}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}

// ──────────────────────────────────────────────────────────────
// GroupRow — read-only な1グループ表示
// ──────────────────────────────────────────────────────────────

const GroupRow = React.memo(function GroupRow({
  group,
  isHot,
  accent,
  border,
  surface,
  textBase,
  search,
  duplicateUids,
}) {
  const finalized = group.status === "finalized";
  const s = (search || "").trim().toLowerCase();
  const memberMatches = (m) => {
    if (!s) return false;
    return (
      (m.name || "").toLowerCase().includes(s) ||
      (m.uid || "").toLowerCase().includes(s)
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: `${isHot ? 2 : 1}px solid ${
          isHot ? "#ffb300" : finalized ? `${accent}55` : border
        }`,
        bgcolor: isHot ? "#fff8e1" : surface,
        transition: "background-color 0.4s ease, border-color 0.4s ease",
        boxShadow: isHot ? "0 0 0 4px #ffb30022" : "none",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: 1 }}
        flexWrap="wrap"
        useFlexGap
      >
        <GroupsRoundedIcon sx={{ color: accent, fontSize: 20 }} />
        <Tooltip title="グループページを開く">
          <Typography
            component={RouterLink}
            to={`/groups2026/${group.id}`}
            sx={{
              fontWeight: 800,
              color: textBase,
              textDecoration: "none",
              borderBottom: "1px dashed rgba(0,0,0,0.25)",
              cursor: "pointer",
              "&:hover": {
                color: accent,
                borderBottomColor: accent,
              },
            }}
          >
            {group.name || "(無名)"}
          </Typography>
        </Tooltip>
        <Chip
          size="small"
          label={`${group.members.length}人`}
          sx={{
            fontWeight: 700,
            bgcolor: `${accent}14`,
            color: accent,
          }}
        />
        {finalized ? (
          <Chip
            size="small"
            icon={<CheckCircleRoundedIcon style={{ color: "#2e7d32" }} />}
            label="確定"
            sx={{
              fontWeight: 800,
              bgcolor: "#2e7d3214",
              color: "#2e7d32",
              border: "1px solid #2e7d3233",
            }}
          />
        ) : (
          <Chip
            size="small"
            label="下書き"
            sx={{
              fontWeight: 800,
              bgcolor: "rgba(0,0,0,0.06)",
              color: "rgba(0,0,0,0.65)",
            }}
          />
        )}
        {isHot && (
          <Chip
            size="small"
            label="更新"
            sx={{
              fontWeight: 800,
              bgcolor: "#ffb30022",
              color: "#a06900",
              border: "1px solid #ffb30055",
            }}
          />
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.55)" }}>
          地域：{group.region || "（未指定）"}
        </Typography>
      </Stack>

      {group.members.length === 0 ? (
        <Typography
          variant="caption"
          sx={{ color: "rgba(0,0,0,0.45)", pl: 0.5 }}
        >
          メンバー未追加
        </Typography>
      ) : (
        <Stack
          direction="row"
          spacing={0.75}
          flexWrap="wrap"
          useFlexGap
          sx={{ pl: 0.5 }}
        >
          {group.members.map((m) => {
            const matched = memberMatches(m);
            const isDuplicate =
              finalized && duplicateUids && m.uid && duplicateUids.has(m.uid);
            return (
              <Tooltip
                key={m.uid}
                title={
                  <Box>
                    <div>{m.name || "(名前未設定)"}</div>
                    <div style={{ opacity: 0.7, fontSize: 11 }}>
                      {m.uid}
                    </div>
                    {m.prefecture && (
                      <div style={{ opacity: 0.7, fontSize: 11 }}>
                        {m.prefecture}
                        {m.city ? ` ${m.city}` : ""}
                      </div>
                    )}
                    {isDuplicate && (
                      <div
                        style={{
                          marginTop: 4,
                          color: "#ffb4b4",
                          fontWeight: 700,
                        }}
                      >
                        ⚠ 複数の確定グループに在籍しています
                      </div>
                    )}
                  </Box>
                }
              >
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  sx={{
                    px: 1,
                    py: 0.5,
                    borderRadius: 999,
                    border: isDuplicate
                      ? "2px solid #d32f2f"
                      : matched
                      ? "1px solid #ffb300"
                      : "1px solid rgba(0,0,0,0.1)",
                    bgcolor: isDuplicate
                      ? "#d32f2f14"
                      : matched
                      ? "#fff8e1"
                      : "rgba(0,0,0,0.03)",
                  }}
                >
                  <Avatar
                    src={m.avatar || undefined}
                    alt={m.name || "user"}
                    sx={{ width: 22, height: 22, fontSize: 11 }}
                  >
                    {(m.name || "?").slice(0, 1)}
                  </Avatar>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: isDuplicate ? "#d32f2f" : textBase,
                    }}
                  >
                    {m.name || "(名前未設定)"}
                  </Typography>
                  {isDuplicate && (
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 900,
                        color: "#d32f2f",
                        ml: 0.25,
                      }}
                    >
                      重複！！
                    </Typography>
                  )}
                </Stack>
              </Tooltip>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
});
